import {
    Box,
    CheckIcon,
    Combobox,
    Group,
    MultiSelect,
    Pill,
    PillsInput,
    Text,
    useCombobox,
} from '@mantine/core';
import { useEffect, useState } from 'react';

const FiltersMultiSelect = ({
    options,
    value: externalValue,
    placeholder = 'Select filters',
    maxDisplayValues = 0,
    onChange,
    maw = 200,
}) => {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    const [search, setSearch] = useState('');
    const [value, setValue] = useState(externalValue || []);

    useEffect(() => {
        if (externalValue) {
            setValue(externalValue);
        }
    }, [externalValue]);

    const handleValueSelect = (val, { active }) => {
        setSearch('');

        setValue((current) =>
            active ? current.filter((v) => v != val) : [...current, val]
        );
        if (onChange) {
            const ret = active
                ? value.filter((v) => v != val)
                : [...value, val];
            onChange(ret);
        }
    };

    const handleValueRemove = (val) => {
        const newValues = value.filter((v) => v !== val);
        setValue(newValues);
        if (onChange) {
            onChange(newValues);
        }
    };

    const values = value.map((item) => (
        <Pill
            key={item}
            withRemoveButton
            onRemove={() => handleValueRemove(item)}>
            {item}
        </Pill>
    ));

    const renderOption = (item) => (
        <Combobox.Option
            value={item.value}
            key={item.value}
            active={value.includes(item.value)}>
            <Group gap={4}>
                {value.includes(item.value) ? <CheckIcon size={12} /> : null}
                <span>{item.label}</span>
            </Group>
        </Combobox.Option>
    );

    const comboboxOptions = options
        .filter((item) => item != null)
        .filter((item) => {
            if (!search) {
                return true;
            }

            if (item.group) {
                return item.items.some((item) =>
                    item.label
                        ?.toLowerCase()
                        .includes(search.trim().toLowerCase())
                );
            }

            return item.label
                ?.toLowerCase()
                .includes(search.trim().toLowerCase());
        })
        .map((item) => {
            if (item.group) {
                return (
                    <Combobox.Group label={item.group}>
                        {item.items
                            ?.filter(
                                (item) =>
                                    !search ||
                                    item.label
                                        ?.toLowerCase()
                                        .includes(search.trim().toLowerCase())
                            )
                            .map(renderOption)}
                    </Combobox.Group>
                );
            }
            return renderOption(item);
        });

    const displayValues = maxDisplayValues
        ? values.slice(0, maxDisplayValues)
        : values;

    return (
        <Group
            mah={20}
            justify='start'
            align='flex-start'>
            <Box maw={maw}>
                <Combobox
                    store={combobox}
                    onOptionSubmit={handleValueSelect}
                    withinPortal={true}
                    zIndex={50000}
                    maw={maw}
                    w={maw}>
                    <Combobox.DropdownTarget>
                        <PillsInput onClick={() => combobox.openDropdown()}>
                            <Pill.Group>
                                {values.length > 0 ? (
                                    <Pill>{value.length} filters</Pill>
                                ) : null}
                                <Combobox.EventsTarget>
                                    <PillsInput.Field
                                        onFocus={() => combobox.openDropdown()}
                                        onBlur={() => combobox.closeDropdown()}
                                        value={search}
                                        placeholder={
                                            values.length === 0
                                                ? placeholder ||
                                                  'Search filters'
                                                : null
                                        }
                                        onChange={(event) => {
                                            combobox.updateSelectedOptionIndex();
                                            setSearch(
                                                event.currentTarget.value
                                            );
                                        }}
                                    />
                                </Combobox.EventsTarget>
                            </Pill.Group>
                        </PillsInput>
                    </Combobox.DropdownTarget>

                    <Combobox.Dropdown>
                        {comboboxOptions}
                        <Combobox.Options>
                            {search.trim().length > 0 &&
                                comboboxOptions.length === 0 && (
                                    <Combobox.Empty>
                                        Nothing found
                                    </Combobox.Empty>
                                )}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>
            </Box>
        </Group>
    );
};
export default FiltersMultiSelect;
